import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { claimsAtom, holdsIdentityAtom } from '../stores/auth/atoms';
import { apiBaseUrlAtom } from '../stores/sdk/atoms';
import { Button } from '@localagent/ui/components/button';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { useDensity } from '../providers/DensityProvider';
import { getSDK } from '../services/sdk-instance';
import { useSignOut } from '../hooks/useSignOut';
import { HealthCheckResponse } from '../types/api-types';
import { BillingSection } from './billing/BillingSection';
import { UsageSection } from './billing/UsageSection';
import { RoleManagementSection } from './account/RoleManagementPanel';
import { WorkstationTermsSection } from './account/WorkstationTermsSection';
import { OrganizationAdminSection } from './Admin/OrganizationAdminSection';
import { OrgSwitcherSection } from './OrgSwitcher';
import { LiveErrorList } from './errors/LiveErrorList';
import { ErrorHistoryTab } from './errors/ErrorHistoryTab';
import { ShogunChopSection } from './shogunchop/ShogunChopSection';
import { ItInstallerSection } from './ItInstallerSection';
import { ExperienceModeToggle } from './experience/ExperienceModeToggle';
import { SettingsChrome } from './experience/SettingsChrome';
import { useDesktopExtensions } from '../providers/DesktopExtensionsProvider';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useExperienceMode } from '../hooks/useExperienceMode';
import { useUpdatePreferences, useUserPreferences } from '../hooks/useUserPreferences';
import {
  clampSessionDurationMinutes,
  DEFAULT_SESSION_DURATION_MINUTES,
  MAX_SESSION_DURATION_MINUTES,
  MIN_SESSION_DURATION_MINUTES,
} from '../auth/sessionDuration';
import { useAcceptWorkstationTerms, useWorkstationTerms } from '../hooks/useWorkstationTerms';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import type { ExecutionCadenceStep } from '@localagent/sdk';
import { ExecutionCadenceSection } from './ExecutionCadenceSection';

export function SettingsView() {
  useCatalogSubscription('orgRoster');
  useCatalogSubscription('billing');
  const [searchParams] = useSearchParams();
  const apiBaseUrl = useAtomValue(apiBaseUrlAtom);
  const { density, setDensity } = useDensity();
  const claims = useAtomValue(claimsAtom);
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const desktopExtensions = useDesktopExtensions();
  const prefsQuery = useUserPreferences();
  const updatePrefs = useUpdatePreferences();
  const experience = useExperienceMode();
  const { signOut, isSigningOut } = useSignOut();
  const [healthStatus, setHealthStatus] = useState<{ status: 'checking' | 'online' | 'offline'; health?: HealthCheckResponse; error?: string } | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [durationDraft, setDurationDraft] = useState<string | null>(null);
  const [cadenceSweep, setCadenceSweep] = useState<ExecutionCadenceStep | null>(null);
  const [cadenceReaper, setCadenceReaper] = useState<ExecutionCadenceStep | null>(null);
  const [cadenceError, setCadenceError] = useState<string | null>(null);
  const [cadenceSaving, setCadenceSaving] = useState(false);
  const [cadenceLoaded, setCadenceLoaded] = useState(false);
  const termsQuery = useWorkstationTerms(isAuthenticated ? claims?.accountId ?? '' : '');
  const acceptTerms = useAcceptWorkstationTerms(isAuthenticated ? claims?.accountId ?? '' : '');
  const previewCardOn = useFeatureFlag('settings_preview_card');

  const kongApiUrl = apiBaseUrl ?? '';
  const sessionDuration =
    durationDraft ??
    String(
      clampSessionDurationMinutes(
        prefsQuery.data?.session_duration_minutes ?? DEFAULT_SESSION_DURATION_MINUTES,
      ),
    );

  const checkHealth = async () => {
    if (apiBaseUrl === null) {
      return;
    }

    setIsChecking(true);
    setHealthStatus({ status: 'checking' });

    try {
      const healthResult = await getSDK().device.healthCheck();
      setHealthStatus({
        status: healthResult?.status === 'healthy' ? 'online' : 'offline',
        health: { status: healthResult.status as 'healthy' | 'unhealthy', timestamp: new Date().toISOString() },
      });
    } catch (error) {
      setHealthStatus({
        status: 'offline',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    setIsChecking(false);
  };

  useEffect(() => {
    if (searchParams.get('section') !== 'errors') return;
    document.getElementById('errors')?.scrollIntoView();
  }, [searchParams]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    getSDK()
      .workManagement.getExecutionCadence()
      .then((cadence) => {
        if (cancelled) {
          return;
        }
        setCadenceSweep(cadence.schedule_sweep);
        setCadenceReaper(cadence.project_run_reaper);
        setCadenceLoaded(true);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setCadenceError(error instanceof Error ? error.message : 'Failed to load execution cadence');
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const saveSessionDuration = () => {
    const parsed = Number(sessionDuration);
    if (!Number.isFinite(parsed)) {
      throw new Error('Session duration must be a number');
    }
    const clamped = clampSessionDurationMinutes(parsed);
    updatePrefs.mutate(
      { session_duration_minutes: clamped },
      {
        onSuccess: () => setDurationDraft(null),
      },
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl space-y-8">
          <SettingsChrome
            mode={experience.mode}
            settingsExtras={desktopExtensions?.settingsExtras}
            display={
              <section className="space-y-4">
                <div>
                  <h2 className="text-title-2 font-semibold">Display</h2>
                  <p className="text-body text-muted-foreground">
                    UI density controls text size and spacing
                  </p>
                </div>
                <div className="rounded-lg bg-card p-4 space-y-4">
                  <div>
                    <span className="text-label font-medium text-muted-foreground">Density</span>
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant={density === 'compact' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setDensity('compact')}
                      >
                        Compact
                      </Button>
                      <Button
                        variant={density === 'large' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setDensity('large')}
                      >
                        Large
                      </Button>
                    </div>
                  </div>
                  <ExperienceModeToggle
                    mode={experience.mode}
                    onChange={experience.setMode}
                    disabled={experience.mode === undefined}
                    error={experience.error?.message ?? null}
                  />
                </div>
              </section>
            }
            backendConnection={
              <section className="space-y-4" data-testid="settings-backend-connection">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-title-2 font-semibold">Backend Connection</h2>
                    <p className="text-body text-muted-foreground">
                      Discovered from .env.endpoints file on startup
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {healthStatus && (
                      <span
                        className={`flex h-2 w-2 rounded-full ${
                          healthStatus.status === 'online'
                            ? 'bg-success'
                            : healthStatus.status === 'checking'
                            ? 'bg-warning animate-pulse'
                            : 'bg-error'
                        }`}
                        title={healthStatus.status}
                      />
                    )}
                    <Button onClick={() => { void checkHealth(); }} disabled={isChecking} size="sm">
                      {isChecking ? 'Checking...' : 'Check Health'}
                    </Button>
                  </div>
                </div>

                <div className="rounded-lg bg-card">
                  <div className="p-4">
                    <span className="text-label font-medium text-muted-foreground">Kong API</span>
                    <code className="mt-1 block rounded bg-muted px-2 py-1 text-body">
                      {kongApiUrl || 'Not discovered'}
                    </code>
                  </div>
                </div>
              </section>
            }
            shogunChop={<ShogunChopSection />}
            midSections={
              <>
                <ItInstallerSection />
                <BillingSection />
                <UsageSection />
                <OrganizationAdminSection />
                <RoleManagementSection />
                {isAuthenticated && previewCardOn && (
                  <section className="space-y-4" data-testid="flag-gated-element">
                    <div className="rounded-lg bg-card p-4">
                      <h2 className="text-title-2 font-semibold">Live preview</h2>
                      <p className="text-body text-muted-foreground">
                        This section is flag-gated and appeared without a reload.
                      </p>
                    </div>
                  </section>
                )}
                {isAuthenticated && claims?.isPlatformAdmin && (
                  <section className="space-y-4" data-testid="settings-platform-section">
                    <div>
                      <h2 className="text-title-2 font-semibold">Platform</h2>
                      <p className="text-body text-muted-foreground">
                        Platform-wide operational surfaces
                      </p>
                    </div>
                    <div className="rounded-lg bg-card p-4">
                      <Link
                        to="/settings/releases"
                        className="text-label font-medium underline-offset-4 hover:underline"
                        data-testid="releases-nav-link"
                      >
                        Releases
                      </Link>
                      <p className="text-body text-muted-foreground">
                        Published feature flags — read-only viewer
                      </p>
                    </div>
                  </section>
                )}
              </>
            }
            errorHistory={
              <>
                <LiveErrorList />
                {experience.mode === 'advanced' ? <ErrorHistoryTab /> : null}
              </>
            }
            afterError={
              <>
                {isAuthenticated && claims && termsQuery.data && (
                  <WorkstationTermsSection
                    terms={termsQuery.data}
                    accepting={acceptTerms.isPending}
                    error={acceptTerms.error?.message ?? termsQuery.error?.message}
                    onAccept={() => acceptTerms.mutate({
                      version: termsQuery.data.version,
                      sha256: termsQuery.data.sha256,
                    })}
                  />
                )}

                {isAuthenticated && claims && termsQuery.isLoading && (
                  <LoadingState variant="inline" label="Loading cloud workstation terms…" />
                )}

                {isAuthenticated && claims && termsQuery.isError && (
                  <p className="text-body text-destructive" role="alert">
                    Cloud workstation terms could not be loaded: {termsQuery.error.message}
                  </p>
                )}

                {isAuthenticated && (
                  <section className="space-y-4" data-testid="session-duration-section">
                    <div>
                      <h2 className="text-title-2 font-semibold">Session duration</h2>
                      <p className="text-body text-muted-foreground">
                        How long your signed-in session lasts (minutes). Default is 30 days
                        ({DEFAULT_SESSION_DURATION_MINUTES}). Allowed range{' '}
                        {MIN_SESSION_DURATION_MINUTES}–{MAX_SESSION_DURATION_MINUTES}.
                      </p>
                    </div>
                    <div className="rounded-lg bg-card p-4 flex flex-wrap items-end gap-3">
                      <label className="space-y-1 text-body">
                        <span className="font-medium text-muted-foreground">Minutes</span>
                        <input
                          data-testid="session-duration-input"
                          type="number"
                          min={MIN_SESSION_DURATION_MINUTES}
                          max={MAX_SESSION_DURATION_MINUTES}
                          value={sessionDuration}
                          onChange={(e) => setDurationDraft(e.target.value)}
                          className="block w-40 rounded bg-muted px-2 py-1"
                        />
                      </label>
                      <Button
                        size="sm"
                        data-testid="session-duration-save"
                        disabled={updatePrefs.isPending}
                        onClick={() => saveSessionDuration()}
                      >
                        {updatePrefs.isPending ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  </section>
                )}

                {isAuthenticated && cadenceLoaded && cadenceSweep && cadenceReaper && (
                  <ExecutionCadenceSection
                    sweep={cadenceSweep}
                    reaper={cadenceReaper}
                    saving={cadenceSaving}
                    error={cadenceError}
                    onSweepChange={setCadenceSweep}
                    onReaperChange={setCadenceReaper}
                    onSave={(cadence) => {
                      setCadenceSaving(true);
                      setCadenceError(null);
                      getSDK()
                        .workManagement.setExecutionCadence(cadence)
                        .then((saved) => {
                          setCadenceSweep(saved.schedule_sweep);
                          setCadenceReaper(saved.project_run_reaper);
                        })
                        .catch((error: unknown) => {
                          setCadenceError(
                            error instanceof Error ? error.message : 'Failed to save execution cadence',
                          );
                        })
                        .finally(() => setCadenceSaving(false));
                    }}
                  />
                )}
                {isAuthenticated && cadenceError && !cadenceLoaded && (
                  <p className="text-body text-destructive" role="alert">
                    {cadenceError}
                  </p>
                )}
              </>
            }
            tenantIds={
              isAuthenticated && claims ? (
                <div data-testid="settings-tenant-ids" className="space-y-3">
                  <div>
                    <h2 className="text-title-2 font-semibold">Authenticated Tenant</h2>
                    <p className="text-body text-muted-foreground">
                      Tenant identity derives from your current Stytch session.
                    </p>
                  </div>
                  <div>
                    <span className="text-label font-medium text-muted-foreground">Organization ID</span>
                    <code className="mt-1 block rounded bg-muted px-2 py-1 text-body">{claims.orgId}</code>
                  </div>
                  <div>
                    <span className="text-label font-medium text-muted-foreground">Account ID</span>
                    <code className="mt-1 block rounded bg-muted px-2 py-1 text-body">{claims.accountId}</code>
                  </div>
                  <div className="flex flex-wrap gap-2 text-caption text-muted-foreground">
                    <span>Roles: {claims.roles?.length ? claims.roles.join(', ') : 'none'}</span>
                    {claims.isTestOrg && <span className="rounded bg-muted px-2 py-1">test org</span>}
                    {claims.isTestAccount && <span className="rounded bg-muted px-2 py-1">test account</span>}
                    {claims.isTestUser && <span className="rounded bg-muted px-2 py-1">test user</span>}
                    {claims.expiresAt && (
                      <span>
                        expires {new Date(claims.expiresAt * 1000).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              ) : null
            }
            tenantActions={
              <div className="rounded-lg bg-card p-4 space-y-3">
                {isAuthenticated && claims ? (
                  <>
                    <OrgSwitcherSection />
                    <Button variant="destructive" size="sm" onClick={() => { void signOut(); }} disabled={isSigningOut}>
                      {isSigningOut ? 'Signing out...' : 'Sign Out'}
                    </Button>
                  </>
                ) : (
                  <div className="text-body text-muted-foreground">
                    Not authenticated. Use the login flow on launch to establish a session.
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}
